import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import './CarouselDialog.scss';
import type { EventPhoto } from '../../types';
import { Button } from '@fastnorth/react-cerberus/Button';
import Details from './components/details/Details';
import { useTranslation } from 'react-i18next';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

type CarouselProps = {
  photo: EventPhoto;
  loadingMore?: boolean;
  onPrev?: (photo: EventPhoto) => void;
  onNext?: (photo: EventPhoto) => void;
  onClose?: () => void;
};

const CarouselDialog = ({ photo, onPrev, onNext, onClose, loadingMore }: CarouselProps): ReactElement => {
  const [open, setOpen] = useState<boolean>(true);
  const { t } = useTranslation();
  const keyDownListener = useCallback(
    ({ code }: KeyboardEvent) => {
      switch (code) {
        case 'ArrowLeft':
          if (onPrev) onPrev(photo);
          break;
        case 'ArrowRight':
          if (onNext) onNext(photo);
          break;
      }
    },
    [photo, onPrev, onNext],
  );

  useEffect(() => {
    document.addEventListener('keydown', keyDownListener);
    return () => {
      document.removeEventListener('keydown', keyDownListener);
    };
  });
  return (
    <Dialog.Root
      onOpenChange={(value) => {
        setOpen(value);
        if (!value && onClose) onClose();
      }}
      open={open}
      modal={false}>
      <Dialog.Portal>
        <Dialog.Content className={'carousel__dialog-content'} onInteractOutside={(e) => e.preventDefault()}>
          {onPrev && (
            <Button
              variant={'unstyled'}
              className={'prev'}
              iconLeft={loadingMore ? 'spinner' : 'chevron-left'}
              onClick={() => onPrev && onPrev(photo)}
              title={`${t('photos_ui.photo_modal.previous')}`}
            />
          )}
          <div className={'carousel__photo-container'}>
            <img className={'carousel__photo'} src={photo.processedFile.uri} alt={'photo of people in race'} />
          </div>
          {onNext && (
            <Button
              disabled={loadingMore}
              variant={'unstyled'}
              iconRight={loadingMore ? 'spinner' : 'chevron-right'}
              className={'next'}
              onClick={() => onNext && onNext(photo)}
              title={`${t('photos_ui.photo_modal.next')}`}
            />
          )}
          <Dialog.Close asChild>
            <div className={'close'}>
              <span className={'fa fa-2x fa-times'} />
            </div>
          </Dialog.Close>
          <div className={'carousel__footer'}>
            <Details tags={photo.tags} />
          </div>
          <VisuallyHidden>
            <Dialog.Title></Dialog.Title>
          </VisuallyHidden>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default CarouselDialog;
